/**
 * Render only the active device-switcher variant so a single <h1> exists.
 *
 * The PaceCore Blocks device switcher ships separate desktop and mobile copies
 * of a section (for example the hero cover) and hides the inactive copy with
 * CSS at each breakpoint. Because both copies stay in the markup, a hero that
 * repeats its headline emits two <h1> tags at once, which SEO audits flag as
 * duplicate top-level headings on pages such as About, Learning Center, and
 * Guided Coaching.
 *
 * The server (`pacecore_stash_first_paint_device_variant`) already renders the
 * viewport-appropriate copy for the detected device and wraps the other copy in
 * an inert <template>, so the first-paint markup has a single real <h1>. This
 * module hydrates that stashed copy and takes over from there: it keeps only the
 * variant matching the current viewport in the live DOM and swaps copies when a
 * breakpoint is crossed. It also works on its own if the server ever renders
 * both copies live (for example uncached responses or non-hero content).
 *
 * Detached copies are held in memory and a comment placeholder marks each
 * original position, so a variant can be reinserted exactly where it belongs.
 *
 * The breakpoints intentionally match the theme's `_responsive-visibility`
 * overrides (`.98` boundaries) so the JavaScript swap lines up exactly with the
 * CSS that hides each variant and no gap or overlap can occur.
 *
 * @since 1.0.0
 */

// Device-switcher classes added by the PaceCore Blocks mobile-display
// enhancement. Each marks the viewport range where a wrapper is hidden.
const hideOnPhoneClass = 'pacecore-blocks-be-d-phone-none';
const hideOnTabletClass = 'pacecore-blocks-be-d-tablet-none';
const hideOnDesktopClass = 'pacecore-blocks-be-d-desktop-none';

const deviceSwitcherSelector = `.${hideOnPhoneClass}, .${hideOnTabletClass}, .${hideOnDesktopClass}`;

// Class the server adds to the inert <template> that holds the first-paint
// inactive variant.
const stashedTemplateSelector = 'template.device-variant-stashed';

// Match the CSS breakpoints exactly. Phone and tablet are explicit ranges and
// anything wider is treated as desktop.
const phoneQuery = window.matchMedia('(max-width: 767.98px)');
const tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 991.98px)');

/**
 * Decide whether a wrapper is hidden by the device switcher right now.
 *
 * Mirrors the CSS rules: a wrapper is hidden when its "hide on X" class matches
 * the current viewport bucket. Desktop is the implicit fallback when neither the
 * phone nor tablet query matches.
 *
 * @param {Element} wrapper The device-switcher wrapper to evaluate.
 * @return {boolean} True when the wrapper should be absent at this viewport.
 */
function isHiddenAtCurrentViewport(wrapper) {
	if (phoneQuery.matches) {
		return wrapper.classList.contains(hideOnPhoneClass);
	}

	if (tabletQuery.matches) {
		return wrapper.classList.contains(hideOnTabletClass);
	}

	return wrapper.classList.contains(hideOnDesktopClass);
}

/**
 * Build the managed variant list from server-stashed and live copies.
 *
 * Server-stashed copies live inside inert <template> elements: we pull the
 * variant element out (it stays detached from the document) and swap the
 * template for a comment placeholder. Live copies are variants the server left
 * in the document: we mark their position with a placeholder and keep the
 * reference. Both cases end up as `{ element, placeholder }` entries that the
 * sync step can attach or detach uniformly.
 *
 * @return {Array<{element: Element, placeholder: Comment}>} Managed variants.
 */
function collectManagedVariants() {
	const managed = [];

	// Adopt the first-paint copies the server stashed in inert templates.
	document.querySelectorAll(stashedTemplateSelector).forEach((template) => {
		const element = template.content.firstElementChild;

		if (
			!element ||
			!element.matches(deviceSwitcherSelector) ||
			!element.querySelector('h1')
		) {
			return;
		}

		const placeholder = document.createComment('device-variant-placeholder');
		template.replaceWith(placeholder);

		// The element still belongs to the template's detached fragment, so it is
		// not part of the live document until it is reinserted.
		managed.push({ element, placeholder });
	});

	// Manage variants the server rendered live. Only the outermost wrapper that
	// carries an <h1> is handled so nested wrappers are not detached twice.
	Array.from(document.querySelectorAll(deviceSwitcherSelector))
		.filter((wrapper) => wrapper.querySelector('h1'))
		.filter((wrapper) => !wrapper.parentElement?.closest(deviceSwitcherSelector))
		.forEach((wrapper) => {
			const placeholder = document.createComment('device-variant-placeholder');
			wrapper.before(placeholder);

			managed.push({ element: wrapper, placeholder });
		});

	return managed;
}

/**
 * Swap device-switcher variants so a single <h1> renders at every viewport.
 */
export function initHeroSingleH1() {
	const managed = collectManagedVariants();

	if (!managed.length) {
		return;
	}

	const syncAll = () => {
		// Detach everything that should be hidden before attaching what should be
		// shown, so the document never holds two <h1> tags at the same instant.
		managed.forEach((entry) => {
			if (isHiddenAtCurrentViewport(entry.element) && entry.element.isConnected) {
				entry.element.remove();
			}
		});

		managed.forEach((entry) => {
			if (!isHiddenAtCurrentViewport(entry.element) && !entry.element.isConnected) {
				entry.placeholder.after(entry.element);
			}
		});
	};

	// Apply the correct variant now and again whenever a breakpoint is crossed.
	syncAll();
	phoneQuery.addEventListener('change', syncAll);
	tabletQuery.addEventListener('change', syncAll);
}
